import { type FC, useMemo } from 'react';
import {
  type Column,
  EditableTable,
  Typography,
} from '@timeedit/ui-components';
import {
  Badge,
  Collapse,
  type CollapseProps,
} from '@timeedit/ui-components/antd';
import type { ErrorTableData } from '../hooks/useErrorTableData';
import './JobErrorTable.scss';

const { Text } = Typography;

type ItemType = NonNullable<CollapseProps['items']>[number];

export const compareExtId = (a?: string, b?: string) =>
  (a ?? '').localeCompare(b ?? '', undefined, { numeric: true });

const COLUMNS: Column<ErrorTableData>[] = [
  {
    key: 'extId',
    title: 'Ext.Id',
    dataIndex: 'extId',
    sorter: (a, b) => compareExtId(a.extId, b.extId),
  },
  {
    key: 'column',
    title: 'Column',
    dataIndex: 'column',
    sorter: (a, b) => (a.column ?? '').localeCompare(b.column ?? ''),
  },
  {
    key: 'cell',
    title: 'Cell',
    dataIndex: 'cell',
    sorter: (a, b) => (a.cell ?? '').localeCompare(b.cell ?? ''),
  },
  {
    key: 'errorCode',
    title: 'Error Code',
    dataIndex: 'errorCode',
    sorter: (a, b) => (a.errorCode ?? 0) - (b.errorCode ?? 0),
  },
  {
    key: 'message',
    title: 'Message',
    dataIndex: 'message',
    sorter: (a, b) => (a.message ?? '').localeCompare(b.message ?? ''),
  },
];

const rowKey = (row: ErrorTableData) =>
  JSON.stringify([
    row.kind,
    row.extId,
    row.column,
    row.cell,
    row.errorCode,
    row.message,
  ]);

type ObjectGroup = { key: string; extId?: string; issues: ErrorTableData[] };
const groupByObject = (rows: ErrorTableData[]): ObjectGroup[] => {
  const groups = new Map<string, ObjectGroup>();
  const fileLevel: ObjectGroup[] = [];

  rows.forEach((row, index) => {
    if (row.extId == null) {
      fileLevel.push({ key: `row-${index}`, issues: [row] });
      return;
    }

    const group = groups.get(row.extId);

    if (group) group.issues.push(row);
    else
      groups.set(row.extId, {
        key: `object-${row.extId}`,
        extId: row.extId,
        issues: [row],
      });
  });

  return Array.from(groups.values()).concat(fileLevel);
};

/** One line per issue, so a cell reads down in step with its neighbours. */
const lines = (values: (string | number | undefined)[]) =>
  values.map((value, index) => (
    <div key={index} className="job-error-table-line">
      {value ?? <>&nbsp;</> /* Don't collapse empty rows */}
    </div>
  ));

const GROUP_COLUMNS: Column<ObjectGroup>[] = [
  {
    key: 'extId',
    title: 'Ext.Id',
    dataIndex: 'extId',
    sorter: (a, b) => compareExtId(a.extId, b.extId),
    defaultSortOrder: 'ascend',
  },
  {
    key: 'column',
    title: 'Column',
    render: (_, group) => lines(group.issues.map((i) => i.column)),
    sorter: (a, b) =>
      (a.issues[0].column ?? '').localeCompare(b.issues[0].column ?? ''),
  },
  {
    key: 'cell',
    title: 'Cell',
    render: (_, group) => lines(group.issues.map((i) => i.cell)),
    sorter: (a, b) =>
      (a.issues[0].cell ?? '').localeCompare(b.issues[0].cell ?? ''),
  },
  {
    key: 'errorCode',
    title: 'Error Code',
    render: (_, group) => lines(group.issues.map((i) => i.errorCode)),
    sorter: (a, b) =>
      (a.issues[0].errorCode ?? 0) - (b.issues[0].errorCode ?? 0),
  },
  {
    key: 'message',
    title: 'Message',
    render: (_, group) => lines(group.issues.map((i) => i.message)),
    sorter: (a, b) => a.issues[0].message.localeCompare(b.issues[0].message),
  },
];

const issueColumns = (rows: ErrorTableData[]) =>
  COLUMNS.filter(
    (col) =>
      (col.key !== 'extId' || rows.some((d) => d.extId)) &&
      (col.key !== 'column' || rows.some((d) => d.column)) &&
      (col.key !== 'cell' || rows.some((d) => d.cell)) &&
      (col.key !== 'errorCode' || rows.some((d) => d.errorCode))
  );

const JobErrorTable: FC<{
  data: ErrorTableData[];
  title: string;
  /** Collapse the rows to one per object, its problems listed within the cells. */
  groupByObject?: boolean;
}> = (props) => {
  const groups = useMemo(
    () => (props.groupByObject ? groupByObject(props.data) : []),
    [props.groupByObject, props.data]
  );

  const items = useMemo<ItemType[]>(() => {
    const table = props.groupByObject ? (
      <EditableTable
        columns={GROUP_COLUMNS.filter(
          (col) =>
            (col.key !== 'extId' || props.data.some((d) => d.extId)) &&
            (col.key !== 'column' || props.data.some((d) => d.column)) &&
            (col.key !== 'cell' || props.data.some((d) => d.cell)) &&
            (col.key !== 'errorCode' || props.data.some((d) => d.errorCode))
        )}
        dataSource={groups}
        rowKey={(group) => group.key}
      />
    ) : (
      <EditableTable
        columns={issueColumns(props.data)}
        dataSource={props.data}
        rowKey={rowKey}
      />
    );

    return [
      {
        label: (
          <>
            <Badge
              count={props.groupByObject ? groups.length : props.data.length}
              size="small"
              style={{ marginRight: 5 }}
            />
            <Text strong>{props.title}</Text>
          </>
        ),
        children: table,
      },
    ];
  }, [props.data, props.title, props.groupByObject, groups]);

  if (props.data.length === 0) return null;

  return <Collapse className="job-error-table" items={items} />;
};

export default JobErrorTable;
